import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { accountBrandsService } from '@/services/accountBrandsService';
import {
  AccountBrand,
  Brand,
  BrandsListFilters,
  CreateBrandData,
  UpdateBrandData,
} from '@/types/accountBrands';

/**
 * Query key factory for account brands
 */
export const brandsKeys = {
  all: ['account-brands'] as const,
  lists: () => [...brandsKeys.all, 'list'] as const,
  list: (filters?: BrandsListFilters) => [...brandsKeys.lists(), filters] as const,
  details: () => [...brandsKeys.all, 'detail'] as const,
  detail: (id: number) => [...brandsKeys.details(), id] as const,
  available: () => [...brandsKeys.all, 'available'] as const,
  availableSearch: (search?: string) => [...brandsKeys.available(), search] as const,
};

/**
 * Hook to fetch list of brands
 */
export const useBrands = (
  filters?: BrandsListFilters,
  options?: Omit<UseQueryOptions<AccountBrand[]>, 'queryKey' | 'queryFn'>
) => {
  const enabled = options?.enabled !== false && !!filters?.account_id;

  return useQuery<AccountBrand[]>({
    queryKey: brandsKeys.list(filters),
    queryFn: () => accountBrandsService.getBrands(filters),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    ...options,
  });
};

/**
 * Hook to fetch a specific brand
 */
export const useBrand = (
  id: number,
  options?: Omit<UseQueryOptions<AccountBrand>, 'queryKey' | 'queryFn'>
) => {
  const enabled = options?.enabled !== false && id > 0;

  return useQuery<AccountBrand>({
    queryKey: brandsKeys.detail(id),
    queryFn: () => accountBrandsService.getBrand(id),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    ...options,
  });
};

/**
 * Hook to fetch available brands for autocomplete/dropdown
 */
export const useAvailableBrands = (
  search?: string,
  options?: Omit<UseQueryOptions<Brand[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<Brand[]>({
    queryKey: brandsKeys.availableSearch(search),
    queryFn: () => accountBrandsService.getAvailableBrands(search),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    ...options,
  });
};

/**
 * Hook to create a new brand
 */
export const useCreateBrand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBrandData) => accountBrandsService.createBrand(data),
    onSuccess: (newBrand) => {
      // Invalidate all brands lists to refetch
      queryClient.invalidateQueries({
        queryKey: brandsKeys.lists(),
      });

      // Optionally, add the new brand to the cache
      queryClient.setQueryData(brandsKeys.detail(newBrand.id), newBrand);
    },
    onError: (error) => {
      console.error('[useCreateBrand] Mutation error:', error);
    },
  });
};

/**
 * Hook to update an existing brand
 */
export const useUpdateBrand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateBrandData }) =>
      accountBrandsService.updateBrand(id, data),
    onSuccess: (updatedBrand, variables) => {
      // Update the detail cache
      queryClient.setQueryData(brandsKeys.detail(variables.id), updatedBrand);

      // Invalidate all lists to refetch
      queryClient.invalidateQueries({
        queryKey: brandsKeys.lists(),
      });
    },
    onError: (error) => {
      console.error('[useUpdateBrand] Mutation error:', error);
    },
  });
};

/**
 * Hook to delete a brand
 */
export const useDeleteBrand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => accountBrandsService.deleteBrand(id),
    onSuccess: (_, deletedId) => {
      // Remove from detail cache
      queryClient.removeQueries({
        queryKey: brandsKeys.detail(deletedId),
      });

      // Invalidate all lists to refetch
      queryClient.invalidateQueries({
        queryKey: brandsKeys.lists(),
      });
    },
    onError: (error) => {
      console.error('[useDeleteBrand] Mutation error:', error);
    },
  });
};
